import React, { useEffect, useMemo, useState } from 'react';
import { BarChart2, CheckCircle2, ClipboardList, Clock3, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { formatCurrencyCRC, formatNumber, getCurrentWeekRangeIso, getTodayRangeIso } from '../../utils/erpLocale';

type DashboardStats = {
  aplicacionesHoy: number;
  pendientes: number;
  enProgreso: number;
  completadas: number;
  costoSemana: number;
  conEvidencia: number;
  totalSemana: number;
};

type TrendPoint = {
  label: string;
  value: number;
};

type RecentRow = {
  id: string;
  boleta: string;
  fecha: string;
  costo: number;
  evidencia: boolean;
};

type AplicacionRow = {
  id: string | number;
  numero?: string | null;
  fecha_aplicacion?: string | null;
  fecha_plan?: string | null;
  created_at?: string | null;
  estado?: string | null;
};

const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

function buildLast7Days() {
  const days: { key: string; label: string; date: Date }[] = [];
  const today = new Date();
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
    days.push({ key, label: DAY_LABELS[d.getDay()], date: d });
  }
  return days;
}

function toDayKey(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

function getFecha(row: AplicacionRow) {
  return String(row.fecha_aplicacion || row.fecha_plan || row.created_at || '');
}

function isMissingTableError(error: any) {
  const code = String(error?.code || '').toUpperCase();
  const msg = String(error?.message || '').toLowerCase();
  return code === '42P01' || code === 'PGRST205' || msg.includes('could not find the table');
}

async function fetchBoletasEstadoCount(empresaId: string | number, estado: string) {
  const res = await supabase
    .from('app_boletas')
    .select('id', { count: 'exact', head: true })
    .eq('empresa_id', empresaId)
    .eq('estado', estado);

  if (!res.error) return { count: res.count || 0 };
  if (isMissingTableError(res.error)) return { count: 0 };
  return { count: 0, error: res.error };
}

async function fetchAplicacionesRows(
  empresaId: string | number,
  {
    select,
    fromIso,
    toIso,
    orderDescByFecha = false,
    limit,
  }: {
    select: string;
    fromIso?: string;
    toIso?: string;
    orderDescByFecha?: boolean;
    limit?: number;
  }
) {
  let query = supabase.from('app_boletas').select(select).eq('empresa_id', empresaId);

  if (fromIso) query = query.gte('fecha_plan', fromIso.slice(0, 10));
  if (toIso) query = query.lt('fecha_plan', toIso.slice(0, 10));
  if (orderDescByFecha) query = query.order('fecha_plan', { ascending: false });
  if (typeof limit === 'number') query = query.limit(limit);

  const res = await query;
  if (res.error) {
    if (isMissingTableError(res.error)) return { data: [] as AplicacionRow[] };
    return { data: [] as AplicacionRow[], error: res.error };
  }
  return { data: ((res.data || []) as unknown) as AplicacionRow[] };
}

async function fetchCampoByBoletas(empresaId: string | number, boletaIds: Array<string | number>) {
  if (!boletaIds.length) return { data: [] as any[] };
  const res = await supabase
    .from('app_boleta_campo')
    .select('boleta_id, inicio_at, fin_at')
    .eq('empresa_id', empresaId)
    .in('boleta_id', boletaIds);

  if (res.error) {
    if (isMissingTableError(res.error)) return { data: [] as any[] };
    return { data: [] as any[], error: res.error };
  }
  return { data: res.data || [] };
}

export function ApplicacionesDashboard() {
  const empresaId = useEmpresaId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    aplicacionesHoy: 0,
    pendientes: 0,
    enProgreso: 0,
    completadas: 0,
    costoSemana: 0,
    conEvidencia: 0,
    totalSemana: 0,
  });
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [recent, setRecent] = useState<RecentRow[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!empresaId) {
        setError('Empresa no definida para cargar datos.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { startIso, endIso } = getTodayRangeIso();
        const week = getCurrentWeekRangeIso();
        const sevenDays = buildLast7Days();
        const sevenStart = new Date(sevenDays[0].date);
        sevenStart.setHours(0, 0, 0, 0);

        const [
          aplicacionesRowsHoyRes,
          pendientesRes,
          enProgresoRes,
          completadasRes,
          weekRowsRes,
          last7RowsRes,
          recentRes,
        ] = await Promise.all([
          fetchAplicacionesRows(empresaId, {
            select: 'id, numero, fecha_plan, fecha_aplicacion, created_at, estado',
            fromIso: startIso,
            toIso: endIso,
          }),
          fetchBoletasEstadoCount(empresaId, 'pendiente'),
          fetchBoletasEstadoCount(empresaId, 'en_progreso'),
          fetchBoletasEstadoCount(empresaId, 'aplicada'),
          fetchAplicacionesRows(empresaId, {
            select: 'id, numero, fecha_plan, fecha_aplicacion, created_at, estado',
            fromIso: week.startIso,
            toIso: week.endIso,
          }),
          fetchAplicacionesRows(empresaId, {
            select: 'id, numero, fecha_plan, fecha_aplicacion, created_at, estado',
            fromIso: sevenStart.toISOString(),
          }),
          fetchAplicacionesRows(empresaId, {
            select: 'id, numero, fecha_plan, fecha_aplicacion, created_at, estado',
            orderDescByFecha: true,
            limit: 5,
          }),
        ]);

        const firstError =
          aplicacionesRowsHoyRes.error ||
          pendientesRes.error ||
          enProgresoRes.error ||
          completadasRes.error ||
          weekRowsRes.error ||
          last7RowsRes.error ||
          recentRes.error;

        if (firstError) throw firstError;

        const weekRows = weekRowsRes.data || [];
        const recentRows = recentRes.data || [];
        const campoIds = Array.from(new Set([...weekRows, ...recentRows].map((row) => row.id)));
        const campoRes = await fetchCampoByBoletas(empresaId, campoIds);
        if (campoRes.error) throw campoRes.error;
        const boletasConCampo = new Set((campoRes.data || []).map((row: any) => String(row.boleta_id)));
        const totalSemana = weekRows.length;
        const conEvidencia = weekRows.filter((r) => boletasConCampo.has(String(r.id))).length;
        const costoSemana = 0;

        const trendMap = new Map<string, number>();
        for (const day of sevenDays) trendMap.set(day.key, 0);
        for (const row of last7RowsRes.data || []) {
          const key = toDayKey(getFecha(row));
          if (!key || !trendMap.has(key)) continue;
          trendMap.set(key, (trendMap.get(key) || 0) + 1);
        }
        setTrend(
          sevenDays.map((d) => ({
            label: d.label,
            value: trendMap.get(d.key) || 0,
          }))
        );

        setRecent(
          recentRows.map((row: any) => ({
            id: String(row.id || ''),
            boleta: String(row.numero || 'Sin boleta'),
            fecha: getFecha(row),
            costo: 0,
            evidencia: boletasConCampo.has(String(row.id)),
          }))
        );

        setStats({
          aplicacionesHoy: (aplicacionesRowsHoyRes.data || []).length,
          pendientes: pendientesRes.count || 0,
          enProgreso: enProgresoRes.count || 0,
          completadas: completadasRes.count || 0,
          costoSemana,
          conEvidencia,
          totalSemana,
        });
      } catch (err: any) {
        setError(err?.message || 'No fue posible cargar el dashboard.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [empresaId]);

  const evidenciaPct = useMemo(() => {
    if (!stats.totalSemana) return 0;
    return Math.round((stats.conEvidencia / stats.totalSemana) * 100);
  }, [stats.conEvidencia, stats.totalSemana]);

  const maxTrendValue = useMemo(() => {
    const maxValue = Math.max(...trend.map((t) => t.value), 1);
    return maxValue;
  }, [trend]);

  const estadoTotal = stats.pendientes + stats.enProgreso + stats.completadas;
  const pendientePct = estadoTotal > 0 ? (stats.pendientes / estadoTotal) * 100 : 0;
  const progresoPct = estadoTotal > 0 ? (stats.enProgreso / estadoTotal) * 100 : 0;
  const completadaPct = estadoTotal > 0 ? (stats.completadas / estadoTotal) * 100 : 0;

  if (loading) {
    return <div className="p-6 text-sm">Cargando dashboard...</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>
            Dashboard de Aplicaciones
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            Actividad operacional de la semana en campo
          </p>
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg border px-3 py-2 text-xs"
          style={{ borderColor: '#7f1d1d', background: 'rgba(127,29,29,0.18)', color: '#fecaca' }}
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div
          className="rounded-lg p-4"
          style={{
            background: 'var(--tone-success-bg)',
            border: '1px solid var(--tone-success-border)',
          }}
        >
          <p className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1" style={{ color: 'var(--tone-success-text)' }}>
            <ClipboardList size={14} /> Aplicaciones hoy
          </p>
          <p className="text-4xl font-bold mt-2" style={{ color: 'var(--tone-success-ink)' }}>
            {formatNumber(stats.aplicacionesHoy)}
          </p>
        </div>

        <div
          className="rounded-lg p-4"
          style={{
            background: 'var(--tone-warn-bg)',
            border: '1px solid var(--tone-warn-border)',
          }}
        >
          <p className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1" style={{ color: 'var(--tone-warn-text)' }}>
            <Clock3 size={14} /> Boletas pendientes
          </p>
          <p className="text-4xl font-bold mt-2" style={{ color: 'var(--tone-warn-ink)' }}>
            {formatNumber(stats.pendientes)}
          </p>
        </div>

        <div
          className="rounded-lg p-4"
          style={{
            background: 'var(--tone-info-bg)',
            border: '1px solid var(--tone-info-border)',
          }}
        >
          <p className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1" style={{ color: 'var(--tone-info-text)' }}>
            <BarChart2 size={14} /> En progreso
          </p>
          <p className="text-4xl font-bold mt-2" style={{ color: 'var(--tone-info-ink)' }}>
            {formatNumber(stats.enProgreso)}
          </p>
        </div>

        <div
          className="rounded-lg p-4"
          style={{
            background: 'var(--tone-purple-bg)',
            border: '1px solid var(--tone-purple-border)',
          }}
        >
          <p className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1" style={{ color: 'var(--tone-purple-text)' }}>
            <CheckCircle2 size={14} /> Completadas
          </p>
          <p className="text-4xl font-bold mt-2" style={{ color: 'var(--tone-purple-ink)' }}>
            {formatNumber(stats.completadas)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>
            Actividad de aplicaciones - ultimos 7 dias
          </p>
          <div className="h-44 flex items-end gap-2">
            {trend.map((point) => {
              const height = Math.max(8, Math.round((point.value / maxTrendValue) * 100));
              return (
                <div key={point.label} className="flex-1 flex flex-col items-center justify-end gap-2">
                  <div className="text-[11px] font-semibold" style={{ color: 'var(--ink-muted)' }}>
                    {point.value}
                  </div>
                  <div
                    className="w-full rounded-t-md transition-all"
                    style={{
                      height: `${height}%`,
                      minHeight: 8,
                      background: 'linear-gradient(180deg, #16a34a 0%, #1f7a45 100%)',
                    }}
                  />
                  <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>
                    {point.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>
            Evidencia y costo semanal
          </p>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div
              className="rounded-lg px-3 py-3"
              style={{
                background: 'var(--tone-success-bg)',
                border: '1px solid var(--tone-success-border)',
              }}
            >
              <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--tone-success-text)' }}>
                Costo semanal
              </p>
              <p className="text-xl font-bold mt-1" style={{ color: 'var(--ink)' }}>
                {formatCurrencyCRC(stats.costoSemana)}
              </p>
            </div>
            <div
              className="rounded-lg px-3 py-3"
              style={{
                background: 'var(--tone-info-bg)',
                border: '1px solid var(--tone-info-border)',
              }}
            >
              <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--tone-info-text)' }}>
                Cobertura
              </p>
              <p className="text-xl font-bold mt-1" style={{ color: 'var(--ink)' }}>
                {formatNumber(evidenciaPct)}%
              </p>
            </div>
          </div>

          <div className="rounded-full overflow-hidden" style={{ height: 10, background: 'var(--surface-overlay)' }}>
            <div
              style={{
                width: `${evidenciaPct}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
              }}
            />
          </div>
          <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
            <ImageIcon size={12} />
            {formatNumber(stats.conEvidencia)} de {formatNumber(stats.totalSemana)} aplicaciones semanales tienen evidencia
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>
            Estado de boletas
          </p>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <div
              className="rounded-md p-2 text-center"
              style={{
                background: 'var(--tone-warn-bg)',
                border: '1px solid var(--tone-warn-border)',
              }}
            >
              <p className="text-xl font-bold" style={{ color: 'var(--tone-warn-text)' }}>{formatNumber(stats.pendientes)}</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Pendiente</p>
            </div>
            <div
              className="rounded-md p-2 text-center"
              style={{
                background: 'var(--tone-info-bg)',
                border: '1px solid var(--tone-info-border)',
              }}
            >
              <p className="text-xl font-bold" style={{ color: 'var(--tone-info-text)' }}>{formatNumber(stats.enProgreso)}</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>En progreso</p>
            </div>
            <div
              className="rounded-md p-2 text-center"
              style={{
                background: 'var(--tone-success-bg)',
                border: '1px solid var(--tone-success-border)',
              }}
            >
              <p className="text-xl font-bold" style={{ color: 'var(--tone-success-text)' }}>{formatNumber(stats.completadas)}</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Completada</p>
            </div>
          </div>

          <div className="rounded-full overflow-hidden" style={{ height: 10, background: 'var(--surface-overlay)' }}>
            <div className="flex h-full">
              <div style={{ width: `${pendientePct}%`, background: '#f59e0b' }} />
              <div style={{ width: `${progresoPct}%`, background: '#3b82f6' }} />
              <div style={{ width: `${completadaPct}%`, background: '#16a34a' }} />
            </div>
          </div>
        </div>

        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>
            Ultimas aplicaciones registradas
          </p>

          {recent.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
              No hay aplicaciones recientes registradas.
            </p>
          ) : (
            <div className="space-y-2">
              {recent.map((row) => (
                <div
                  key={row.id}
                  className="rounded-md p-3 flex items-center justify-between gap-2"
                  style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>
                      {row.boleta}
                    </p>
                    <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                      {row.fecha ? new Date(row.fecha).toLocaleString('es-CR') : 'Sin fecha'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                      {formatCurrencyCRC(row.costo)}
                    </p>
                    <p className="text-[11px]" style={{ color: row.evidencia ? '#4ade80' : '#f59e0b' }}>
                      {row.evidencia ? 'Con evidencia' : 'Sin evidencia'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-md p-3" style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>Total semanal</p>
            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--ink)' }}>{formatNumber(stats.totalSemana)}</p>
          </div>
          <div className="rounded-md p-3" style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>Costo promedio</p>
            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--ink)' }}>
              {formatCurrencyCRC(stats.totalSemana ? stats.costoSemana / stats.totalSemana : 0)}
            </p>
          </div>
          <div className="rounded-md p-3" style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>Tasa completadas</p>
            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--ink)' }}>
              {formatNumber(estadoTotal ? (stats.completadas / estadoTotal) * 100 : 0, 1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApplicacionesDashboard;
